import { type ReactNode } from 'react';
import useUser from '@/lib/hooks/use-user';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock } from 'lucide-react';

interface FeatureGateProps {
    children: ReactNode;
    featureName: string;
    description?: string;
    requireFullAccount?: boolean;
}

export function FeatureGate({
    children,
    featureName,
    description = "Sign in to unlock this feature and save your progress.",
    requireFullAccount = false
}: FeatureGateProps) {
    const { user, isUserLoading } = useUser();

    if (isUserLoading) {
        return <div className="p-8 flex items-center justify-center">Loading...</div>;
    }

    const isAuthorized = user && (!requireFullAccount || !user.isAnonymous);

    if (isAuthorized) {
        return <>{children}</>;
    }

    return (
        <div className="flex items-center justify-center p-6 h-full min-h-[400px]">
            <Card className="w-full max-w-md border-dashed border-2">
                <CardHeader className="text-center">
                    <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                        <Lock className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-2xl font-light">{featureName}</CardTitle>
                    <CardDescription className="text-base mt-2">
                        {requireFullAccount && user?.isAnonymous
                            ? "This premium feature requires a full account to track your progress permanently."
                            : description}
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                    <Button asChild className="w-full" size="lg">
                        <a href="/login">
                            {requireFullAccount && user?.isAnonymous ? "Upgrade Account" : "Sign In to Access"}
                        </a>
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                        Your discoveries are safe. Sign in with Google to sync them across all your devices.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
